import { Stack, styled, Typography } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { DataTable } from "@/components/custom/data-table";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import CustomPagination from "@/components/custom/custom-pagination";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import { useEffect, useMemo, useRef, useState } from "react";
import { useChargebacks } from "@/hooks/use-chargebacks";
import { useChargebackStats } from "@/hooks/use-chargeback-stats";
import { formatCurrency } from "@/utils/format-currency";
import { ChargebackColumns } from "./_components/chargeback-columns";
import { ChargebacksSummary, chargebackStatusOptions } from "../data";
import CreateDisputeDialog from "./_components/create-dispute-dialog";
import { CustomButton } from "@/components/custom/custom-button";

const SectionRoot = styled("section")(({ theme }) => ({
  margin: theme.spacing(2),
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "16px 20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const Chargebacks = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const searchValue = searchParams.get("search") || "";
  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);
  const status = searchParams.get("status");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [presetTransaction, setPresetTransaction] = useState(null);
  const consumed = useRef(false);


  useEffect(() => {
    if (location.state?.createDisputeTransaction && !consumed.current) {
      consumed.current = true;
      setPresetTransaction(location.state.createDisputeTransaction);
      setDialogOpen(true);
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location, navigate]);

  const handleOpenBlank = () => {
    setPresetTransaction(null);
    setDialogOpen(true);
  };

  const queryParams = {
    page: currentPage,
    size: pageSize,
    status: status || undefined,
    search: searchValue,
  };

  const {
    data: chargebacks,
    isLoading,
    isError,
  } = useChargebacks(true, queryParams);

  const { stats, isLoading: isStatsLoading } = useChargebackStats();

  const columns = useMemo(() => ChargebackColumns, []);

  return (
    <SectionRoot>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {ChargebacksSummary.map(({ title, objKeyRepr, icon, isCurrency }, idx) => (
            <CustomSimpleCard
              key={`${objKeyRepr}-${idx}`}
              title={title}
              icon={icon}
              isLoading={isStatsLoading}
            >
              <Typography variant="h6">
                {isCurrency
                  ? formatCurrency(stats?.[objKeyRepr] ?? 0)
                  : (stats?.[objKeyRepr] ?? 0)}
              </Typography>
            </CustomSimpleCard>
          ))}
        </Stack>

        <Card>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <CustomSearchInput paramKey="search" placeholder="Search chargebacks..." />
            <CustomSelectInput
              paramKey="status"
              label="Status"
              placeholder="All Status"
              options={chargebackStatusOptions}
            />
            <CustomButton onClick={handleOpenBlank} sx={{ height: 35 }}>
              Create Dispute
            </CustomButton>
          </Stack>

          <DataTable
            data={chargebacks?.data?.data ?? []}
            columns={columns}
            isLoading={isLoading}
            isError={isError}
            noDataMessage="No chargebacks found."
          />

          <CustomPagination
            total={chargebacks?.data?.totalCount ?? 0}
            perPageOptions={[5, 10, 20, 50, 100]}
          />
        </Card>
      </Stack>
      <CreateDisputeDialog
        open={dialogOpen}
        presetTransaction={presetTransaction}
        onClose={() => setDialogOpen(false)}
      />
    </SectionRoot>
  );
};
